import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import Button, { ButtonType } from "../Assets/Button";
import {
  IAddress,
  ServiceTicketType,
} from "../../models/serviceManagement.model";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import { getStatesData } from "../../store/slices/kanbanSlice";
import { getInverterModelNo } from "../../store/slices/serviceManagementSlice";

interface AddServiceTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketType: ServiceTicketType;
  setTicketType: (v: ServiceTicketType) => void;
  epcName: string;
  epcMobileNumber: string;
  epcPincode: string;
  siteAddress: string;
  customerName: string;
  customerMobileNumber: string;
  customerAddress: IAddress;
  srNo: string;
  loggerSerialNo: string | undefined;
  complainNote: string | undefined;
  inverterModel?: string | undefined;
  selectedErrorCode: Record<string, string> | undefined;
  setEpcName: (v: string) => void;
  setEpcMobileNumber: (v: string) => void;
  setEpcPincode: (v: string) => void;
  setSiteAddress: (v: string) => void;
  setCustomerName: (v: string) => void;
  setCustomerMobileNumber: (v: string) => void;
  setCustomerAddress: (v: IAddress) => void;
  setSrNo: (v: string) => void;
  setLoggerSerialNo: (v: string) => void;
  setComplainNote: (v: string) => void;
  setInverterModel: (v: string | undefined) => void;
  selectedPhase: string;
  setSelectedPhase: (v: "single" | "three" | "") => void;
  setSelectedErrorCode: (v: Record<string, string> | undefined) => void;
  loading: boolean;
  handleAddServiceTicket: () => void;
}

const AddServiceTicketModal: React.FC<AddServiceTicketModalProps> = ({
  isOpen,
  onClose,
  ticketType,
  setTicketType,
  epcName,
  epcMobileNumber,
  epcPincode,
  siteAddress,
  customerName,
  customerMobileNumber,
  customerAddress,
  srNo,
  loggerSerialNo,
  complainNote,
  inverterModel,
  selectedErrorCode,
  setEpcName,
  setEpcMobileNumber,
  setEpcPincode,
  setSiteAddress,
  setCustomerName,
  setCustomerMobileNumber,
  setCustomerAddress,
  setSrNo,
  setLoggerSerialNo,
  setComplainNote,
  setInverterModel,
  selectedPhase,
  setSelectedPhase,
  setSelectedErrorCode,
  loading,
  handleAddServiceTicket,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const statesData = useSelector((state: RootState) => state.kanban.statesData);

  const inverterModelNumbers = useSelector(
    (state: RootState) => state.serviceManagement.inverterModelNumbers,
  );

  const serviceErrorCodes = useSelector(
    (state: RootState) => state.serviceManagement.serviceErrorCodes,
  );

  useEffect(() => {
    if (statesData.length === 0) {
      dispatch(getStatesData());
    }
    if (
      isOpen &&
      inverterModelNumbers.singlePhase.length === 0 &&
      inverterModelNumbers.threePhase.length === 0
    ) {
      dispatch(getInverterModelNo());
    }
  }, [dispatch, statesData.length, isOpen, inverterModelNumbers]);

  const handleOnClose = () => {
    onClose();
  };

  const handlePhaseSelection = (phase: "single" | "three") => {
    setSelectedPhase(phase);
    setInverterModel(undefined);
    setSelectedErrorCode(undefined);
  };

  const isMonitoringTicket = ticketType === ServiceTicketType.monitoring;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 ">
      <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl p-6 relative animate-fadeIn max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-lg font-semibold text-gray-800">
            Create Service Request
          </h2>
          <button
            onClick={handleOnClose}
            className="p-2 rounded-full hover:bg-gray-200 transition"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>

        <div className="border border-gray-300 rounded-xl p-4 bg-gray-50/60 mb-3">
          <h3 className="font-semibold text-gray-700 text-sm mb-3">
            Ticket Type <span className="text-red-500">*</span>
          </h3>

          <select
            value={ticketType}
            onChange={(e) => setTicketType(e.target.value as ServiceTicketType)}
            className="w-full text-xs px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:outline-none"
          >
            {Object.values(ServiceTicketType).map((type) => (
              <option key={type} value={type}>
                {type === ServiceTicketType.monitoring
                  ? "Monitoring Issue"
                  : "Inverter Fault"}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-3">
          {!isMonitoringTicket && (
            <div className="border border-gray-300 rounded-xl p-4 bg-gray-50/60">
              <h3 className="font-semibold text-gray-700 text-sm mb-3">
                EPC Details <span className="text-gray-400">(Optional)</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-600 text-xs font-semibold">
                    EPC Name
                  </label>
                  <input
                    value={epcName}
                    onChange={(e) => setEpcName(e.target.value)}
                    className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white"
                  />
                </div>

                <div>
                  <label className="text-gray-600 text-xs font-semibold">
                    EPC Mobile Number
                  </label>
                  <input
                    value={epcMobileNumber}
                    onChange={(e) => setEpcMobileNumber(e.target.value)}
                    className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white"
                  />
                </div>

                <div>
                  <label className="text-gray-600 text-xs font-semibold">
                    EPC Address
                  </label>
                  <input
                    value={siteAddress}
                    onChange={(e) => setSiteAddress(e.target.value)}
                    className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white"
                  />
                </div>

                <div>
                  <label className="text-gray-600 text-xs font-semibold">
                    EPC Pincode
                  </label>
                  <input
                    value={epcPincode}
                    onChange={(e) => setEpcPincode(e.target.value)}
                    className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white"
                  />
                </div>
              </div>
            </div>
          )}

          <div className="border border-gray-300 rounded-xl p-4 bg-gray-50/60">
            <h3 className="font-semibold text-gray-700 text-sm mb-3">
              Customer Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-gray-600 text-xs font-semibold">
                  Customer Name <span className="text-red-500">*</span>
                </label>
                <input
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white"
                />
              </div>

              <div>
                <label className="text-gray-600 text-xs font-semibold">
                  Customer Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  value={customerMobileNumber}
                  onChange={(e) => setCustomerMobileNumber(e.target.value)}
                  className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white"
                />
              </div>

              {!isMonitoringTicket && (
                <>
                  <div className="col-span-2">
                    <span className="text-gray-600 text-xs font-semibold">
                      Customer Address Details{" "}
                      <span className="text-red-500">*</span>
                    </span>
                  </div>

                  <select
                    value={customerAddress.state}
                    onChange={(e) =>
                      setCustomerAddress({
                        ...customerAddress,
                        state: e.target.value,
                        district: "",
                      })
                    }
                    className="w-full text-xs px-3 py-2 border rounded-lg bg-white"
                  >
                    <option value="">Select State</option>
                    {statesData?.map((s) => (
                      <option key={s.state} value={s.state.toLowerCase()}>
                        {s.state.capitalizeWords()}
                      </option>
                    ))}
                  </select>

                  {/* District */}
                  <select
                    value={customerAddress.district}
                    onChange={(e) =>
                      setCustomerAddress({
                        ...customerAddress,
                        district: e.target.value,
                      })
                    }
                    disabled={!customerAddress.state}
                    className="w-full text-xs px-3 py-2 border rounded-lg bg-white disabled:bg-gray-200"
                  >
                    <option value="">Select District</option>
                    {statesData
                      .find(
                        (s) => s.state === customerAddress.state.toLowerCase(),
                      )
                      ?.districts?.map((district: string) => (
                        <option key={district} value={district}>
                          {district.capitalizeWords()}
                        </option>
                      ))}
                  </select>

                  <input
                    value={customerAddress.addressLine1}
                    onChange={(e) =>
                      setCustomerAddress({
                        ...customerAddress,
                        addressLine1: e.target.value,
                      })
                    }
                    className="w-full text-xs px-3 py-2 border rounded-lg bg-white"
                    placeholder="Address Line 1"
                  />

                  <input
                    value={customerAddress.pincode}
                    onChange={(e) =>
                      setCustomerAddress({
                        ...customerAddress,
                        pincode: e.target.value,
                      })
                    }
                    className="w-full text-xs px-3 py-2 border rounded-lg bg-white"
                    placeholder="Pincode"
                  />
                </>
              )}
            </div>
          </div>

          <div className="border border-gray-300 rounded-xl p-4 bg-gray-50/60">
            <h3 className="font-semibold text-gray-700 text-sm mb-3">
              Product Details
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-gray-600 text-xs font-semibold">
                  Serial Number <span className="text-red-500">*</span>
                </label>
                <input
                  value={srNo}
                  onChange={(e) => setSrNo(e.target.value)}
                  className="w-full text-xs px-3 py-2 border rounded-lg bg-white"
                />
              </div>

              <div>
                <label className="text-gray-600 text-xs font-semibold">
                  Logger Serial Number{" "}
                  {isMonitoringTicket && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <input
                  value={loggerSerialNo}
                  onChange={(e) => setLoggerSerialNo(e.target.value)}
                  className="w-full text-xs px-3 py-2 border rounded-lg bg-white"
                />
              </div>

              <div>
                <label className="text-gray-600 text-xs font-semibold">
                  Select Phase{" "}
                  {isMonitoringTicket && (
                    <span className="text-red-500"> *</span>
                  )}
                </label>
                <div className="flex gap-1 mt-2">
                  <button
                    onClick={() => handlePhaseSelection("single")}
                    className={`w-full px-4 py-2 text-xs rounded-lg border ${
                      selectedPhase === "single"
                        ? "bg-blue-100 border-blue-500"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    Single Phase
                  </button>
                  <button
                    onClick={() => handlePhaseSelection("three")}
                    className={`w-full px-4 py-2 text-xs rounded-lg border ${
                      selectedPhase === "three"
                        ? "bg-blue-100 border-blue-500"
                        : "bg-white border-gray-300"
                    }`}
                  >
                    Three Phase
                  </button>
                </div>
              </div>

              <div>
                <label className="text-gray-600 text-xs font-semibold">
                  Select Model{" "}
                  {isMonitoringTicket && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <select
                  value={inverterModel}
                  onChange={(e) => setInverterModel(e.target.value)}
                  disabled={!selectedPhase}
                  className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white disabled:bg-gray-200"
                >
                  <option value="">Select Model</option>

                  {selectedPhase === "single" &&
                    inverterModelNumbers.singlePhase.map((modelNo: string) => (
                      <option key={modelNo} value={modelNo}>
                        {modelNo}
                      </option>
                    ))}

                  {selectedPhase === "three" &&
                    inverterModelNumbers.threePhase.map((modelNo: string) => (
                      <option key={modelNo} value={modelNo}>
                        {modelNo}
                      </option>
                    ))}
                </select>
              </div>

              {!isMonitoringTicket && (
                <div className="col-span-2">
                  <label className="text-gray-600 text-xs font-semibold">
                    Select Error Code
                  </label>
                  <select
                    value={selectedErrorCode?.code || ""}
                    onChange={(e) => {
                      const code = e.target.value;

                      const message =
                        selectedPhase === "single"
                          ? serviceErrorCodes.singlePhase[code]
                          : serviceErrorCodes.threePhase[code];

                      setSelectedErrorCode({ code, message });
                    }}
                    disabled={!selectedPhase}
                    className="w-full text-xs mt-1 px-3 py-2 border rounded-lg bg-white disabled:bg-gray-200"
                  >
                    <option value="">Select Error Code</option>

                    {selectedPhase === "single" &&
                      Object.entries(serviceErrorCodes.singlePhase).map(
                        ([code, message]) => (
                          <option key={code} value={code}>
                            {code} — {message}
                          </option>
                        ),
                      )}

                    {selectedPhase === "three" &&
                      Object.entries(serviceErrorCodes.threePhase).map(
                        ([code, message]) => (
                          <option key={code} value={code}>
                            {code} — {message}
                          </option>
                        ),
                      )}
                  </select>
                </div>
              )}
            </div>
          </div>

          <div className="border border-gray-300 rounded-xl p-4 bg-gray-50/60">
            <h3 className="font-semibold text-gray-700 text-sm mb-3">
              Complaint Note
            </h3>

            <input
              value={complainNote}
              onChange={(e) => setComplainNote(e.target.value)}
              className="w-full text-xs px-3 py-2 border rounded-lg bg-white"
              placeholder="Describe the issue..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button
            type={ButtonType.Cancel}
            onClick={handleOnClose}
            className="text-xs"
          >
            Cancel
          </Button>
          <Button
            type={ButtonType.Submit}
            onClick={handleAddServiceTicket}
            disabled={loading}
            className="text-xs"
          >
            {loading ? "Saving..." : "Create Request"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddServiceTicketModal;
